import React, { useState, useEffect, useCallback } from 'react';
import { Inbox, ChevronDown, ChevronUp } from 'lucide-react';

export const RegistrationsHubTab = ({ API_URL, getAuthHeader }) => {
  const [data, setData] = useState({ groups: [], total: 0 });
  const [openGroup, setOpenGroup] = useState(null);
  const [openRow, setOpenRow] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/registrations/overview`, { headers: getAuthHeader() });
      if (r.ok) setData(await r.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fmt = (v) => {
    if (Array.isArray(v)) return v.map(x => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
    if (typeof v === 'object' && v !== null) return JSON.stringify(v);
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    return String(v);
  };

  return (
    <div className="space-y-5" data-testid="registrations-hub">
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl px-6 py-5 text-white">
        <p className="text-[11px] font-black uppercase tracking-[0.22em] text-amber-300 mb-1 flex items-center gap-2">
          <Inbox className="w-3.5 h-3.5" aria-hidden="true" /> Website Registrations
        </p>
        <h3 className="text-lg font-bold">All signups received through the site</h3>
        <p className="text-white/70 text-xs mt-0.5">{data.total} total, grouped by registration type</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {data.groups.map(g => (
          <button key={g.key} onClick={() => { setOpenGroup(openGroup === g.key ? null : g.key); setOpenRow(null); }}
            className={`text-left rounded-xl border p-4 transition-all ${openGroup === g.key ? 'border-teal-500 bg-teal-50 shadow-md' : 'border-slate-200 bg-white hover:border-teal-300'}`}
            data-testid={`hub-card-${g.key}`}>
            <p className="text-2xl font-black text-slate-800">{g.count}</p>
            <p className="text-xs font-semibold text-slate-600 leading-snug">{g.label}</p>
          </button>
        ))}
      </div>

      {data.groups.filter(g => openGroup ? g.key === openGroup : g.count > 0).map(g => (
        <div key={g.key} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`hub-group-${g.key}`}>
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
            <h4 className="text-sm font-bold text-slate-800">{g.label} <span className="text-slate-400 font-normal">({g.count})</span></h4>
          </div>
          {g.count === 0 ? (
            <p className="px-5 py-6 text-sm text-slate-400">No registrations yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 uppercase">
                  <tr>
                    <th className="text-left py-2 px-4">Name</th>
                    <th className="text-left py-2 px-4">Email</th>
                    <th className="text-left py-2 px-4">Details</th>
                    <th className="text-center py-2 px-4">Status</th>
                    <th className="text-center py-2 px-4">Date</th>
                    <th className="text-center py-2 px-4" aria-label="Expand"></th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((r, i) => {
                    const rowKey = `${g.key}-${r.id || i}`;
                    return (
                      <React.Fragment key={rowKey}>
                        <tr className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`hub-row-${rowKey}`}>
                          <td className="py-2.5 px-4 font-medium text-slate-800">{r.name}</td>
                          <td className="py-2.5 px-4 text-slate-600 text-xs">{r.email || '—'}</td>
                          <td className="py-2.5 px-4 text-xs text-slate-500">{r.summary || '—'}</td>
                          <td className="py-2.5 px-4 text-center">
                            {r.status ? <span className="inline-block px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[10px] font-bold uppercase">{String(r.status).replace(/_/g, ' ')}</span> : '—'}
                          </td>
                          <td className="py-2.5 px-4 text-center text-xs text-slate-500">{r.date || '—'}</td>
                          <td className="py-2.5 px-4 text-center">
                            <button onClick={() => setOpenRow(openRow === rowKey ? null : rowKey)} aria-label="Toggle details" className="text-slate-400 hover:text-teal-700 p-1" data-testid={`hub-expand-${rowKey}`}>
                              {openRow === rowKey ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          </td>
                        </tr>
                        {openRow === rowKey && (
                          <tr className="border-t border-slate-100 bg-slate-50/60">
                            <td colSpan={6} className="px-5 py-4">
                              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1.5 text-xs">
                                {Object.entries(r.raw).filter(([k]) => k !== 'id').map(([k, v]) => (
                                  <div key={k} className="flex gap-2">
                                    <dt className="font-semibold text-slate-600 capitalize whitespace-nowrap">{k.replace(/_/g, ' ')}:</dt>
                                    <dd className="text-slate-700 break-words">{fmt(v) || '—'}</dd>
                                  </div>
                                ))}
                              </dl>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
